/**
 * Drive usage over the websocket.
 *
 * The server measures on-disk size by walking the directory tree, which is too
 * slow to fold into the normal clip list REST call. Instead the client asks
 * over the socket and the answers stream back: one message per clip, then a
 * terminator carrying `complete: true`.
 *
 * There is a synchronous mirror at GET /api/usage/cases/{caseId}/clips that
 * returns the same data in one shot, but it blocks the request thread while it
 * walks -- which is why this path exists.
 */

// Inbound envelope command (server -> browser).
export const DRIVE_USAGE = "DRIVE_USAGE";

// Outbound command (browser -> server).
export const DRIVE_USAGE_REQUEST = "DRIVE_USAGE_REQUEST";

// DriveUsageRequestType. ALL_CLIPS_IN_CASE enumerates from the database, so
// every message corresponds to a real clip we can label (CASE enumerates from
// the filesystem and can surface orphaned directories with no name).
export const ALL_CLIPS_IN_CASE = "ALL_CLIPS_IN_CASE";

// DriveUsageScope values.
export const USER_SCOPE = "USER";
export const CASE_SCOPE = "CASE";
export const CLIP_SCOPE = "CLIP";
export const COMMIT_SCOPE = "COMMIT";
export const LOOSE_FILES_SCOPE = "LOOSE_FILES";

// A payload the server can't parse is dropped silently -- it logs and returns
// without answering -- so a request that goes unanswered has to time out on
// its own rather than leaving the UI waiting forever.
export const DRIVE_USAGE_TIMEOUT_MS = 120000;

/**
 * Build a DRIVE_USAGE_REQUEST frame for every clip in a case.
 *
 * Note the double encoding: the inbound message's `payload` is a JSON *string*,
 * not a nested object, because the server runs the outer envelope and the
 * request body through Gson separately.
 */
export const buildDriveUsageRequest = (accessCode, caseId, correlationId) =>
  JSON.stringify({
    authorization: accessCode,
    command: DRIVE_USAGE_REQUEST,
    payload: JSON.stringify({
      type: ALL_CLIPS_IN_CASE,
      caseId: Number(caseId),
      correlationId,
    }),
  });

export const buildDriveUsageRequestUser = (accessCode, userId, correlationId) =>
  JSON.stringify({
    authorization: accessCode,
    command: DRIVE_USAGE_REQUEST,
    payload: JSON.stringify({
      type: USER_SCOPE,
      userId: Number(userId),
      correlationId,
    }),
  });

export const buildDriveUsageRequestCase = (accessCode, caseId, correlationId) =>
  JSON.stringify({
    authorization: accessCode,
    command: DRIVE_USAGE_REQUEST,
    payload: JSON.stringify({
      type: CASE_SCOPE,
      caseId: Number(caseId),
      correlationId,
    }),
  });

/**
 * Bytes to show for a measured clip, or null when the clip has no directory on
 * disk. Distinct from undefined, which means "not measured yet".
 */
export const driveUsageBytes = (usage) => {
  if (!usage) return undefined;
  return usage.exists ? usage.bytes : null;
};
